import axios from 'axios';
import { getDefaultStore, PrimitiveAtom } from 'jotai';
import { getGigyaToken } from './gigya';
import { exchangeGigyaTokenForJWT } from './auth';
import {
  accessTokenAtom,
  authErrorAtom,
  isAuthenticatedAtom,
  lastFailingEndpointAtom,
  invalidSoldToErrorAtom,
  preferredShipToAtom,
  profileCompletionErrorAtom,
  retailUserErrorAtom,
  invalidGroupUserErrorAtom,
  shelterMissingErrorAtom,
} from '../atoms/authAtom';
import { shelterAtom, Shelter as ShelterAtomType } from '../atoms/shelterAtom';
import { OrderStatus, Order, Orders } from '../atoms/ordersAtom';
import { TrackingResponse, TrackingProvider } from '../types/tracking';
import { CutoffDate } from '../atoms/cutoffAtom';
import { BillingShippingDto } from '../atoms/billingShippingAtom';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:3002/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// ==== CIRCUIT BREAKER FOR AUTH FAILURES ====
let authFailureCount = 0;
let lastAuthFailure = 0;
const MAX_AUTH_FAILURES = 3;
const BASE_COOLDOWN_MS = 30000; // 30 seconds
const MAX_COOLDOWN_MS = 300000; // 5 minutes cap
const MISSING_OR_INVALID_SOLD_TO_MESSAGE = 'Missing or invalid sold_to from Gigya account';

function shouldAttemptAuth(): boolean {
  if (authFailureCount >= MAX_AUTH_FAILURES) {
    // Exponential backoff: 30s, 60s, 120s, 240s, capped at 300s
    const retriesBeyondMax = authFailureCount - MAX_AUTH_FAILURES;
    const cooldownMs = Math.min(BASE_COOLDOWN_MS * Math.pow(2, retriesBeyondMax), MAX_COOLDOWN_MS);
    const timeSinceLastFailure = Date.now() - lastAuthFailure;
    if (timeSinceLastFailure < cooldownMs) {
      console.warn(
        `[API Service] Auth circuit breaker active. Waiting ${Math.ceil((cooldownMs - timeSinceLastFailure) / 1000)}s before retry (failures: ${authFailureCount})`
      );
      return false;
    }
    // Allow one retry attempt after cooldown, but don't reset counter
  }
  return true;
}

function recordAuthFailure(): void {
  authFailureCount++;
  lastAuthFailure = Date.now();
  console.warn(`[API Service] Auth failure ${authFailureCount}/${MAX_AUTH_FAILURES}`);
}

function resetAuthFailures(): void {
  authFailureCount = 0;
  lastAuthFailure = 0;
}

// ==== NEW CACHE IMPLEMENTATION FOR BACKEND JWT ====
let backendTokenCache: { token: string; expiresAt: number } | null = null;

// Helper function to set shelter atom while preserving preferred_ship_to
function setShelterAtomPreservingPreferredShipTo(shelterResponse: any): void {
  const store = getDefaultStore();
  const existingShelter = store.get(shelterAtom);
  const shelterData = shelterResponse.data as ShelterAtomType;
  if (existingShelter?.preferred_ship_to) {
    shelterData.preferred_ship_to = existingShelter.preferred_ship_to;
  }
  store.set(shelterAtom, shelterData);
}
let pendingBackendTokenRequest: Promise<string> | null = null;
let hasInvalidSoldToError = false; // Flag to prevent retries after INVALID_SOLD_TO error

type ApiErrorLike = {
  response?: {
    status?: number;
    data?: {
      error?: string;
      soldTo?: string;
      message?: string | { error?: string; message?: string; soldTo?: string };
    };
  };
  message?: string;
};

function getApiErrorMessage(error: ApiErrorLike): string | null {
  const responseData = error.response?.data;

  if (typeof responseData?.message === 'string') {
    return responseData.message;
  }

  if (
    typeof responseData?.message === 'object' &&
    typeof responseData.message?.message === 'string'
  ) {
    return responseData.message.message;
  }

  if (typeof error.message === 'string') {
    return error.message;
  }

  return null;
}

function isInvalidSoldToError(error: ApiErrorLike): boolean {
  const responseData = error.response?.data;
  const nestedErrorCode =
    typeof responseData?.message === 'object' ? responseData.message?.error : undefined;
  return (
    error.response?.status === 403 &&
    (responseData?.error === 'INVALID_SOLD_TO' || nestedErrorCode === 'INVALID_SOLD_TO')
  );
}

function isMissingSoldToAuthError(error: ApiErrorLike): boolean {
  if (error.response?.status !== 401) {
    return false;
  }

  const message = getApiErrorMessage(error)?.toLowerCase() ?? '';
  return (
    message.includes('missing or invalid sold_to') ?? message.includes('missing sold_to identifier')
  );
}

function isTerminalSoldToAuthError(error: ApiErrorLike): boolean {
  return isInvalidSoldToError(error) || isMissingSoldToAuthError(error);
}

function getTerminalSoldToErrorMessage(error: ApiErrorLike): string {
  const responseData = error.response?.data;
  const explicitMessage = getApiErrorMessage(error);

  if (explicitMessage) {
    return explicitMessage;
  }

  if (isInvalidSoldToError(error)) {
    const soldToValue =
      responseData?.soldTo ??
      (typeof responseData?.message === 'object' ? responseData.message?.soldTo : undefined) ??
      'unknown';
    return `Shelter with sold_to ${soldToValue} does not exist.`;
  }

  return MISSING_OR_INVALID_SOLD_TO_MESSAGE;
}

function markTerminalSoldToAuthError(
  store: ReturnType<typeof getDefaultStore>,
  message: string
): void {
  hasInvalidSoldToError = true;
  backendTokenCache = null;
  store.set(invalidSoldToErrorAtom, message);
  store.set(authErrorAtom, message);
  store.set(accessTokenAtom, undefined);
  store.set(isAuthenticatedAtom, false);
}

// Map of known 403 auth error codes to the atoms that trigger their UI
const AUTH_ERROR_CONFIG: Record<string, PrimitiveAtom<boolean>> = {
  NOT_FSL_USER: invalidGroupUserErrorAtom,
  USER_WITH_RETAIL_ACCOUNT: retailUserErrorAtom,
  MISSING_SOLD_TO_OR_POSTALCODE: profileCompletionErrorAtom,
  MISSING_SHELTER_IN_DB: shelterMissingErrorAtom,
  SHELTER_INACTIVE: shelterMissingErrorAtom, // use the same missing atom for inactive shelters
};

// Handle known 403 auth errors by setting the right atom and clearing the token.
// Returns true if the error was handled (caller should rethrow the error).
function handleKnownAuthError(errorCode: string | undefined): boolean {
  const errorAtom = errorCode && AUTH_ERROR_CONFIG[errorCode];
  if (!errorAtom) return false;

  const store = getDefaultStore();
  store.set(errorAtom, true);
  store.set(accessTokenAtom, undefined);
  recordAuthFailure();
  return true;
}

// Helper to decode backend JWT expiration
function getBackendTokenExpiration(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp; // Expiration time in seconds
  } catch (error) {
    console.error('[API Service] Error decoding backend token expiration:', error);
    return Date.now() / 1000 + 300; // Default to 5 minutes
  }
}

// Initialize backend token cache from localStorage on module load
function initializeBackendTokenCache() {
  try {
    const storedAuth = localStorage.getItem('hills_fsl_auth');
    if (!storedAuth) return;

    const { token, expiresAt } = JSON.parse(storedAuth);
    if (token && expiresAt > Date.now()) {
      backendTokenCache = {
        token,
        expiresAt: getBackendTokenExpiration(token),
      };
    }
  } catch {
    // Silently fail - not critical
  }
}
initializeBackendTokenCache();

// Export function to update the backend token cache (used when switching shelters)
export function updateBackendTokenCache(token: string): void {
  const expiresAt = getBackendTokenExpiration(token);
  backendTokenCache = { token, expiresAt };
}

// Centralized backend token fetching with caching
// Exported for use by useAPI hook to prevent duplicate auth calls
export async function getBackendTokenWithCache(): Promise<string> {
  // Check if we've already encountered an INVALID_SOLD_TO error
  if (hasInvalidSoldToError) {
    throw new Error('Terminal sold_to authentication error - authentication blocked');
  }

  // Check circuit breaker
  if (!shouldAttemptAuth()) {
    throw new Error('Authentication temporarily disabled due to repeated failures');
  }

  // Return cached token if valid
  if (backendTokenCache && backendTokenCache.expiresAt > Date.now() / 1000) {
    console.debug('[API Service] Using cached backend token');
    return backendTokenCache.token;
  }

  // Return pending request if exists
  if (pendingBackendTokenRequest) {
    console.debug('[API Service] Returning pending backend token request');
    return pendingBackendTokenRequest;
  }

  // Create new request without async executor
  pendingBackendTokenRequest = (async () => {
    try {
      console.debug('[API Service] Fetching new backend token');
      const gigyaToken = await getGigyaToken();
      const authResponse = await exchangeGigyaTokenForJWT(gigyaToken);

      const token = authResponse.token;
      const expiresAt = getBackendTokenExpiration(token);
      backendTokenCache = { token, expiresAt };

      // Set preferredShipTo from auth response
      const store = getDefaultStore();
      store.set(
        preferredShipToAtom,
        authResponse.preferredShipTo ? String(Number(authResponse.preferredShipTo)) : null
      );

      console.debug('[API Service] New backend token received and cached');
      resetAuthFailures(); // Reset on success
      return token;
    } catch (error) {
      const typedError = error as ApiErrorLike;
      const errorCode = typedError.response?.data?.error;

      if (typedError.response?.status === 403 && handleKnownAuthError(errorCode)) {
        throw error;
      }

      if (isTerminalSoldToAuthError(typedError)) {
        const message = getTerminalSoldToErrorMessage(typedError);
        const store = getDefaultStore();
        markTerminalSoldToAuthError(store, message);
      }
      recordAuthFailure();
      throw error;
    } finally {
      pendingBackendTokenRequest = null;
    }
  })();

  return pendingBackendTokenRequest;
}

// ==== UPDATE REQUEST INTERCEPTOR ====
api.interceptors.request.use(
  async (config) => {
    const store = getDefaultStore();

    // Add locale header to all requests
    const locale = window.fsl_locale || 'en-us';
    config.headers['X-FSL-Locale'] = locale;

    // Check if this endpoint requires authentication
    const isContentstackEndpoint = config.url?.includes('/contentstack/');
    const requiresAuth = !isContentstackEndpoint;

    // If endpoint doesn't require auth, proceed without token
    if (!requiresAuth) {
      console.debug(
        '[API Interceptor] Contentstack endpoint, proceeding without auth:',
        config.url
      );
      return config;
    }

    // Use cached token if available
    if (backendTokenCache && backendTokenCache.expiresAt > Date.now() / 1000) {
      config.headers.Authorization = `Bearer ${backendTokenCache.token}`;
      console.debug('[API Interceptor] Using cached backend token');
      return config;
    }

    let token = store.get(accessTokenAtom);

    // If no token, try to get one using cache (with circuit breaker)
    if (!token) {
      try {
        console.debug('[API Interceptor] No token found, attempting to get one for:', config.url);
        token = await getBackendTokenWithCache();
        store.set(accessTokenAtom, token);
        store.set(authErrorAtom, null);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.warn('[API Interceptor] Failed to get token for request:', config.url, error);

        const typedError = error as ApiErrorLike;

        // Stop immediately for terminal sold_to auth failures (no retries allowed)
        if (isTerminalSoldToAuthError(typedError)) {
          const message = getTerminalSoldToErrorMessage(typedError);
          markTerminalSoldToAuthError(store, message);
          // Throw the error to stop the request entirely and prevent retries
          return Promise.reject(error instanceof Error ? error : new Error(String(error)));
        }

        // If we already flagged terminal sold_to error, block request without attempting fallback.
        if (hasInvalidSoldToError) {
          return Promise.reject(error instanceof Error ? error : new Error(String(error)));
        }

        // Block the request — sending without auth causes 401 → retry loop
        console.warn('[API Interceptor] Token acquisition failed, blocking request:', config.url);
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.debug('[API Interceptor] Request with token:', token.substring(0, 15) + '...');
    } else {
      console.warn('[API Interceptor] No auth token found for request:', config.url);
      return Promise.reject(new Error('No auth token available'));
    }
    return config;
  },
  (error) => {
    console.error('[API Interceptor] Request error:', error);
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
);

// ==== UPDATE RESPONSE INTERCEPTOR ====
// Shared promise so multiple concurrent 401s share a single token refresh
let pendingTokenRefresh: Promise<string> | null = null;

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const store = getDefaultStore();
    const typedError = error as ApiErrorLike;

    // Block all retries when sold_to auth error is terminal (403 INVALID_SOLD_TO or 401 missing sold_to)
    if (isTerminalSoldToAuthError(typedError)) {
      const message = getTerminalSoldToErrorMessage(typedError);
      markTerminalSoldToAuthError(store, message);
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }

    // If the error is due to an expired token (401)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark retry immediately to prevent parallel retries of the same request
      originalRequest._retry = true;

      // Check circuit breaker before attempting refresh
      if (!shouldAttemptAuth()) {
        console.warn(
          '[API Interceptor] Auth circuit breaker active - not attempting token refresh'
        );
        return Promise.reject(new Error('Authentication temporarily disabled'));
      }

      try {
        // Share a single refresh across all concurrent 401 responses
        if (!pendingTokenRefresh) {
          console.debug('[API Interceptor] Starting token refresh...');
          pendingTokenRefresh = getBackendTokenWithCache().finally(() => {
            pendingTokenRefresh = null;
          });
        } else {
          console.debug('[API Interceptor] Joining existing token refresh...');
        }

        const token = await pendingTokenRefresh;

        // Update the token in the store
        store.set(accessTokenAtom, token);
        store.set(authErrorAtom, null);

        // Update the Authorization header and retry
        originalRequest.headers.Authorization = `Bearer ${token}`;
        console.debug('[API Interceptor] Retrying original request:', originalRequest.url);
        return api(originalRequest);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (refreshError: any) {
        console.error('[API Interceptor] Token refresh failed:', refreshError);
        const typedRefreshError = refreshError as ApiErrorLike;

        // Stop token refresh loop on terminal sold_to auth errors
        if (isTerminalSoldToAuthError(typedRefreshError)) {
          const message = getTerminalSoldToErrorMessage(typedRefreshError);
          markTerminalSoldToAuthError(store, message);
          return Promise.reject(error instanceof Error ? error : new Error(String(error)));
        }

        store.set(
          authErrorAtom,
          refreshError instanceof Error ? refreshError.message : 'Token refresh failed'
        );
        store.set(accessTokenAtom, undefined);
        return Promise.reject(
          refreshError instanceof Error ? refreshError : new Error(String(refreshError))
        );
      }
    }

    // Update last failing endpoint for error tracking
    if (error.response && error.config) {
      console.error(`[API Interceptor] API call failed for ${error.config.url}:`, error.response);
      store.set(
        lastFailingEndpointAtom,
        `${error.config.url} - ${error.response.statusText} (${error.response.status})`
      );
    }

    // If it's not a 401 or we've already tried refreshing, just reject
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
);

// ==== ORDERS API FUNCTIONS ====

/**
 * Fetch all orders with tracking URLs
 */
export async function fetchOrders(shipTo?: string): Promise<Orders> {
  try {
    const url = shipTo ? `/orders?shipTo=${encodeURIComponent(shipTo)}` : '/orders';
    const response = await api.get(url);

    const ordersData = response.data?.orders ?? [];
    const typedOrders: Order[] = ordersData.map((order: Record<string, unknown>) => ({
      id: order.id as string,
      status: order.status as OrderStatus,
      estimatedArrivalDate: order.estimatedArrivalDate as string | undefined,
      trackingNumber: order.trackingNumber as string | undefined,
      trackingProvider: order.trackingProvider as TrackingProvider | undefined,
      shiptoName: order.shiptoName as string | undefined,
    }));

    return { orders: typedOrders };
  } catch (error) {
    console.error('[API] Failed to fetch orders:', error);
    throw error;
  }
}

/**
 * Fetch tracking information for a specific order
 * @param orderNumber - The order number to get tracking for
 * @returns Promise with tracking response
 */
export async function fetchOrderTracking(orderNumber: string): Promise<TrackingResponse> {
  const response = await api.get(`/orders/${orderNumber}/tracking`);
  return response.data;
}

export interface ShipToLocation {
  shipTo: string;
  name: string;
  address?: string;
}

export interface ShipToLocations {
  locations: ShipToLocation[];
}

/**
 * Fetch ship-to locations for the current shelter
 * @returns Promise with ship-to locations
 */
export async function fetchShipToLocations(): Promise<ShipToLocations> {
  const jwt = await getGigyaToken();

  if (!jwt) {
    throw new Error('No Gigya JWT token available');
  }

  const store = getDefaultStore();
  let soldTo: string | null = null;

  // Try to get soldTo from shelterAtom first (if already loaded)
  const shelter = store.get(shelterAtom);
  soldTo = shelter?.sold_to ?? null;

  // If not in atom, fetch current shelter data on-demand
  if (!soldTo) {
    try {
      const shelterResponse = await api.get('/shelters/current');
      soldTo = shelterResponse.data.sold_to;

      // Update the atom for future use, preserving preferred_ship_to if it exists
      setShelterAtomPreservingPreferredShipTo(shelterResponse);
    } catch (error) {
      console.error('[fetchShipToLocations] Failed to fetch current shelter:', error);
      throw new Error('Unable to retrieve shelter information');
    }
  }

  if (!soldTo) {
    throw new Error('No soldTo found for current shelter');
  }

  const response = await api.post('/orders/ship-to-locations', {
    soldTo,
    jwt,
  });

  return response.data;
}

/**
 * Fetch billing and shipping information for the current shelter
 * @returns Promise with billing and shipping information
 */
export async function fetchBillingShipping(): Promise<BillingShippingDto> {
  const jwt = await getGigyaToken();

  if (!jwt) {
    throw new Error('No Gigya JWT token available');
  }

  const store = getDefaultStore();
  let soldTo: string | null = null;

  // Try to get soldTo from shelterAtom first (if already loaded)
  const shelter = store.get(shelterAtom);
  soldTo = shelter?.sold_to ?? null;

  // If not in atom, fetch current shelter data on-demand
  if (!soldTo) {
    try {
      const shelterResponse = await api.get('/shelters/current');
      soldTo = shelterResponse.data.sold_to;

      // Update the atom for future use, preserving preferred_ship_to if it exists
      setShelterAtomPreservingPreferredShipTo(shelterResponse);
    } catch (error) {
      console.error('[fetchShipToLocations] Failed to fetch current shelter:', error);
      throw new Error('Unable to retrieve shelter information');
    }
  }

  if (!soldTo) {
    throw new Error('No soldTo found for current shelter');
  }

  const response = await api.post('/account/billing-shipping', {
    soldTo,
    jwt,
  });

  return response.data;
}

export async function fetchCutOffDate(shipTo: string): Promise<CutoffDate> {
  if (!shipTo) {
    throw new Error('No shipTo provided');
  }

  try {
    const response = await api.get(`/orders/cutoff-date?shipTo=${encodeURIComponent(shipTo)}`);

    if (!response.data) {
      throw new Error('No cutoff date found for shipTo');
    }

    return {
      cutoffDate: response.data?.cutoffDate,
      cutoffText: response.data?.cutoffText,
    };
  } catch (error) {
    console.error('[API] Failed to fetch cutoff date:', error);
    throw error;
  }
}

// ==== SHELTERS API FUNCTIONS ====

export interface Shelter {
  id: string;
  name: string;
  sold_to: string;
  join_program_date: string;
  created_at: Date | string;
  updated_at: Date | string;
  /** Absent or true means the shelter is active; false means deactivated. */
  is_active?: boolean;
  /** Absent or false means the shelter reports manually. */
  is_petszel_enabled?: boolean;
}

export interface CreateShelterDto {
  name: string;
  sold_to: string;
  join_program_date: string;
}

export interface UpdateShelterDto {
  name?: string;
  sold_to?: string;
  join_program_date?: string;
}

/**
 * Fetch all shelters
 */
export interface JwtResponse {
  token: string;
  expiresIn: number;
  sold_to: string;
  role: string;
  email?: string;
}

export async function switchShelter(soldTo: string): Promise<JwtResponse> {
  // Construct Gateway URL by removing trailing /api from base URL
  const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3002/api';
  const baseUrl = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;
  const switchShelterUrl = `${baseUrl}/auth/switch-shelter`;

  // Get current token from store
  const store = getDefaultStore();
  const token = backendTokenCache?.token ?? store.get(accessTokenAtom);

  console.group('[API] switchShelter - Request Details');
  console.log('Target sold_to:', soldTo);
  console.log('Gateway URL:', switchShelterUrl);
  console.log('Token available:', !!token);
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      console.log('Current token payload:', {
        sold_to: payload.sold_to,
        role: payload.role,
        email: payload.email,
        exp: new Date(payload.exp * 1000).toISOString(),
      });
    } catch (e) {
      console.warn('Unable to decode token payload:', e);
    }
  }
  console.groupEnd();

  try {
    const response = await axios.post<JwtResponse>(
      switchShelterUrl,
      { sold_to: soldTo },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
      }
    );

    console.log('[API] switchShelter - Success:', {
      new_sold_to: response.data.sold_to,
      role: response.data.role,
      email: response.data.email,
    });

    return response.data;
  } catch (error: unknown) {
    console.group('[API] switchShelter - FAILED');
    console.error('Error object:', error);

    if (axios.isAxiosError(error)) {
      console.error('Status:', error.response?.status);
      console.error('Status text:', error.response?.statusText);
      console.error('Response data:', error.response?.data);
      console.error('Request URL:', error.config?.url);
      console.error('Request headers:', error.config?.headers);
    }

    console.groupEnd();
    throw error;
  }
}

export async function fetchAllShelters(): Promise<Shelter[]> {
  try {
    const response = await api.get('/shelters');
    return response.data ?? [];
  } catch (error) {
    console.error('[API] Failed to fetch shelters:', error);
    throw error;
  }
}

export interface ShelterWithReportCounts extends Shelter {
  petParentReportCount: number;
  adoptionReportCount: number;
}

let pendingSheltersWithReportCountsRequest: Promise<ShelterWithReportCounts[]> | null = null;

export async function fetchAllSheltersWithReportCounts(): Promise<ShelterWithReportCounts[]> {
  if (pendingSheltersWithReportCountsRequest) {
    return pendingSheltersWithReportCountsRequest;
  }

  try {
    pendingSheltersWithReportCountsRequest = (async () => {
      const response = await api.get('/shelters/with-report-counts');
      return response.data ?? [];
    })();

    return await pendingSheltersWithReportCountsRequest;
  } catch (error) {
    console.error('[API] Failed to fetch shelters with report counts:', error);
    throw error;
  } finally {
    pendingSheltersWithReportCountsRequest = null;
  }
}

/**
 * Fetch a single shelter by ID
 */
export async function fetchShelterById(id: string): Promise<Shelter> {
  try {
    const response = await api.get(`/shelters/${id}`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch shelter:', error);
    throw error;
  }
}

/**
 * Create a new shelter
 */
export async function createShelter(shelterData: CreateShelterDto): Promise<Shelter> {
  try {
    const response = await api.post('/shelters', shelterData);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create shelter:', error);
    throw error;
  }
}

/**
 * Update an existing shelter
 */
export async function updateShelter(id: string, shelterData: UpdateShelterDto): Promise<Shelter> {
  try {
    const response = await api.put(`/shelters/${id}`, shelterData);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update shelter:', error);
    throw error;
  }
}

/**
 * Delete a shelter
 */
export async function deleteShelter(id: string): Promise<void> {
  try {
    await api.delete(`/shelters/${id}`);
  } catch (error) {
    console.error('[API] Failed to delete shelter:', error);
    throw error;
  }
}

/**
 * Download shelter template (CSV or XLSX)
 */
export async function downloadShelterTemplate(format: 'csv' | 'xlsx' = 'xlsx'): Promise<Blob> {
  try {
    const response = await api.get(`/shelters/template?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to download template:', error);
    throw error;
  }
}

/**
 * Upload shelter spreadsheet for processing
 */
export async function uploadShelterSpreadsheet(file: File): Promise<{
  success: boolean;
  processedCount: number;
  data: Array<{ name: string; sold_to: string; join_program_date: string }>;
  errors: Array<{ row: number; field: string; message: string }>;
}> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/shelters/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error('[API] Failed to upload spreadsheet:', error);
    throw error;
  }
}

/**
 * Bulk import shelters from processed data
 */
export async function bulkImportShelters(
  shelters: CreateShelterDto[],
  updateOnDuplicate = false
): Promise<{
  success: boolean;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ index: number; sold_to: string; error: string }>;
}> {
  try {
    const response = await api.post(
      `/shelters/bulk-import?updateOnDuplicate=${updateOnDuplicate}`,
      { shelters }
    );

    return response.data;
  } catch (error) {
    console.error('[API] Failed to bulk import shelters:', error);
    throw error;
  }
}

export default api;
